// src/pages/draft_document/TemplateFormFields.jsx
import React, { useEffect, useRef } from "react";
import styles from "./DraftDocumentNew.module.css";
import AttachmentSection from "../../components/attachment/AttachmentSection";
import ServiceGiseongAutoFill from "../../components/document-type/ServiceGiseongAutoFill";
import { EXPENSE_TEMPLATES } from "../../components/preview/expense/expenseTemplates";
import {
  AUTO_SUM_TEMPLATES,
  SOURCE_LIST_ROUTES,
  GASAN_MONTH_TEMPLATES,
  Row,
  YearMonthFields,
  DueDateFields,
  formatDateShort,
  calcDailyWageRow,
  defaultSocialInsuranceRows,
} from "./DraftDocumentNew";

// ✅ "기성계 불러오기" 모달의 연도 셀렉트 — 현재 연도 기준 앞뒤로 몇 년치(GisungHistory.jsx의
// 고정 연도 목록과 달리 매해 수동 갱신할 필요 없도록 현재 연도 기준으로 계산).
const YONGYEOK_GISUNG_YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 3 + i);

export default function TemplateFormFields({
  isDataLoaded,
  draftNo,
  title,
  sourceEndpoint,
  sourceItemId,
  documentType,
  category,
  subcategory,
  draftDate,
  claimDate,
  workMonth,
  vendorInput,
  handleChangeVendor,
  handleVendorKeyDown,
  showVendorList,
  filteredVendors,
  highlightIndex,
  handlePickVendor,
  amount,
  setAmount,
  setTouchedFields,
  payMethodLabel,
  serviceContent,
  setServiceContent,
  templateState,
  setTemplateField,
  detailOption,
  templateInputs,
  setTemplateInput,
  attachments,
  setAttachments,
  desc,
  setDesc,
  setTitle,
  setTableType,
  tableType,
  onSubmit,
  setShowLoadModal,
  showYongyeokGisungModal,
  setShowYongyeokGisungModal,
  yongyeokGisungList,
  yongyeokGisungLoading,
  fetchYongyeokGisungList,
  handlePickYongyeokGisung,
  yongyeokGisungYear,
  setYongyeokGisungYear,
  yongyeokGisungMonth,
  setYongyeokGisungMonth,
  yongyeokGisungDraftedFilter,
  setYongyeokGisungDraftedFilter,
}) {
  // ✅ 거래처 자동완성 목록 — 하이라이트(↓/↑) 이동 시 화면 밖으로 나가지 않도록 자동 스크롤
  const vendorListRef = useRef(null);
  useEffect(() => {
    vendorListRef.current?.children?.[highlightIndex]?.scrollIntoView({ block: "nearest" });
  }, [highlightIndex]);

  return (
        <div className={styles.pane}>

          {/* 기존 입력 폼 */}
          <form className={styles.form} onSubmit={onSubmit}>
            <h3 style={{ marginBottom: "20px" }}>📝 기안서 작성</h3>

            {/* ✅ 불러오기 버튼 (제목 바로 아래) */}
            <button 
              type="button"
              onClick={() => setShowLoadModal(true)}
              className={styles.loadBtn}
              style={{ marginBottom: "20px", width: "100%" }}
            >
              📥 자료 불러오기
            </button>

            {/* 기안번호 - 세로 유지 */}
            <Row label="기안번호(자동)">
              <input 
                value={isDataLoaded ? draftNo : ""} 
                readOnly 
                placeholder="자료를 불러오면 자동 생성됩니다." 
              />
            </Row>

            {/* 제목 - 세로 유지 */}
            <Row label="제목(자동)">
              <input 
                value={isDataLoaded ? title : ""} 
                readOnly 
                placeholder="자료를 불러오면 자동 생성됩니다." 
              />
            </Row>

            {/* ✅ 원본 데이터 열기 - 불러온 원본을 새 탭에서 열람 (현재 작성 중인 기안 상태는 유지) */}
            {isDataLoaded && SOURCE_LIST_ROUTES[sourceEndpoint] && (
              <button
                type="button"
                onClick={() => window.open(`${SOURCE_LIST_ROUTES[sourceEndpoint]}?highlight=${sourceItemId}`, "_blank")}
                className={styles.loadBtn}
                style={{ marginBottom: "20px", width: "100%" }}
              >
                🔗 원본 데이터 열기
              </button>
            )}

            {/* ✅ 자료를 불러온 경우에만 나머지 필드 표시 */}
            {isDataLoaded && (
              <>
              {/* ✅ 용역 기성계 자동완성 (렌더링 없음, side-effect only) */}
                <ServiceGiseongAutoFill
                  documentType={documentType}
                  category={category}
                  detailOption={detailOption}
                  vendor={vendorInput}
                  workMonth={workMonth}
                  setTitle={setTitle}
                  setDesc={setDesc}
                  setAttachments={setAttachments}
                  setTableType={setTableType}
                  setTemplateField={setTemplateField}



                />

                {/* ✅ 용역 수입기안 청구형(공단 계약) 전용 — 기성계 재조회(회차/계약금액/
                    기기성금액 확정). 제목·본문은 회차 확정 전엔 비어있는 게 정상 */}
                {tableType === "yongyeok_claim" && (
                  <button
                    type="button"
                    onClick={() => setShowYongyeokGisungModal(true)}
                    className={styles.loadBtn}
                    style={{ marginBottom: "20px", width: "100%" }}
                  >
                    📋 기성계 불러오기{templateState.yongyeokRound ? ` (현재: 제${templateState.yongyeokRound}회)` : ""}
                  </button>
                )}

                {/* ✅ 불러온 수입 데이터 금액+귀속월과 일치하는 기성계 레코드를 못 찾아
                    최신 회차+1로 폴백했을 때 — 회차가 틀렸을 수 있으니 확인 필요 */}
                {tableType === "yongyeok_claim" && templateState.yongyeokRoundWarning && (
                  <div
                    style={{
                      marginBottom: 20,
                      padding: "10px 14px",
                      background: "#fff3cd",
                      border: "1px solid #ffe08a",
                      borderRadius: 6,
                      color: "#8a6d1a",
                      fontSize: 13,
                      lineHeight: 1.5,
                    }}
                  >
                    ⚠️ 회차 자동 판별 실패 — 확인 필요 (금액·귀속월이 일치하는 기성계 레코드를 찾지 못해 최신 회차+1로 채웠습니다)
                  </div>
                )}

                {showYongyeokGisungModal && (
                  <div className={styles.modalOverlay} onClick={() => setShowYongyeokGisungModal(false)}>
                    <div
                      style={{ background: "#fff", borderRadius: 8, padding: 20, width: 560, maxHeight: "70vh", overflowY: "auto" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <h3 style={{ marginTop: 0, whiteSpace: "nowrap" }}>기성계 이력 불러오기</h3>

                      {/* ✅ 연도/월 셀렉트 + 미작성/작성/전체 토글(기본 미작성) — 값이 바뀔 때마다
                          부모(DraftDocumentNew.jsx)의 useEffect가 서버에 재조회한다. */}
                      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
                        <select
                          value={yongyeokGisungYear}
                          onChange={(e) => setYongyeokGisungYear(e.target.value)}
                          style={{ padding: "6px 8px", fontSize: 13, borderRadius: 6, border: "1px solid #ccc" }}
                        >
                          {YONGYEOK_GISUNG_YEAR_OPTIONS.map((y) => (
                            <option key={y} value={y}>{y}년</option>
                          ))}
                        </select>
                        <select
                          value={yongyeokGisungMonth}
                          onChange={(e) => setYongyeokGisungMonth(e.target.value)}
                          style={{ padding: "6px 8px", fontSize: 13, borderRadius: 6, border: "1px solid #ccc" }}
                        >
                          <option value="">전체 월</option>
                          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                            <option key={m} value={m}>{m}월</option>
                          ))}
                        </select>
                        <div style={{ display: "flex", gap: 4, marginLeft: "auto" }}>
                          {[
                            { value: "undrafted", label: "미작성" },
                            { value: "drafted", label: "작성" },
                            { value: "all", label: "전체" },
                          ].map((t) => (
                            <button
                              key={t.value}
                              type="button"
                              onClick={() => setYongyeokGisungDraftedFilter(t.value)}
                              style={{
                                padding: "6px 12px",
                                fontSize: 13,
                                fontWeight: 600,
                                borderRadius: 6,
                                cursor: "pointer",
                                border: yongyeokGisungDraftedFilter === t.value ? "1px solid #1565C0" : "1px solid #ccc",
                                background: yongyeokGisungDraftedFilter === t.value ? "#1565C0" : "#fff",
                                color: yongyeokGisungDraftedFilter === t.value ? "#fff" : "#37474f",
                              }}
                            >
                              {t.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {yongyeokGisungLoading ? (
                        <div style={{ padding: 24, textAlign: "center", color: "#aaa" }}>불러오는 중...</div>
                      ) : yongyeokGisungList.length === 0 ? (
                        <div style={{ padding: 24, textAlign: "center", color: "#aaa" }}>기성계 이력이 없습니다.</div>
                      ) : (
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                          <thead>
                            <tr>
                              <th style={{ textAlign: "left", padding: "6px 4px", borderBottom: "1px solid #ddd" }}>회차</th>
                              <th style={{ textAlign: "left", padding: "6px 4px", borderBottom: "1px solid #ddd" }}>귀속연월</th>
                              <th style={{ textAlign: "right", padding: "6px 4px", borderBottom: "1px solid #ddd" }}>금회기성금액</th>
                              <th style={{ padding: "6px 4px", borderBottom: "1px solid #ddd" }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {yongyeokGisungList.map((r) => (
                              <tr key={r.id}>
                                <td style={{ padding: "6px 4px" }}>{r.gisung_round}회</td>
                                <td style={{ padding: "6px 4px", whiteSpace: "nowrap" }}>{r.gisung_year}년 {r.gisung_month}월</td>
                                <td style={{ padding: "6px 4px", textAlign: "right" }}>{Number(r.current_amount || 0).toLocaleString()}원</td>
                                <td style={{ padding: "6px 4px" }}>
                                  {r.is_drafted ? (
                                    <span
                                      title={r.drafted_document?.draft_no ? `기안번호: ${r.drafted_document.draft_no}` : "작성됨"}
                                      style={{
                                        display: "inline-block", whiteSpace: "nowrap",
                                        padding: "3px 8px", borderRadius: 12, fontSize: 11, fontWeight: 700,
                                        background: "#d1fae5", color: "#065f46",
                                      }}
                                    >
                                      작성됨{r.drafted_document?.draft_no ? ` · ${r.drafted_document.draft_no}` : ""}
                                    </span>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() => handlePickYongyeokGisung(r)}
                                      style={{ width: "56px", height: "28px" }}
                                    >
                                      선택
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                )}

                {/* 사업구분 + 세부구분 - 한 줄 배치 */}
                <div className={styles.rowDouble}>
                  <div className={styles.rowLabel}>사업구분</div>
                  <div className={styles.rowField}>
                    <input 
                      type="text" 
                      value={category} 
                      readOnly
                      className={styles.loadedData}
                    />
                  </div>
                  
                  <div className={styles.rowLabel}>세부구분</div>
                  <div className={styles.rowField}>
                    {/* ✅ 자료 불러오기 시 원본의 subcategory/detail_type으로만 채워지는 자동 필드 —
                        일부 템플릿(환경/소독 등)은 이 값과의 정확한 문자열 일치로 제목을 분기하므로
                        사용자가 임의로 고치면 그 로직이 깨진다. category/작성일/청구일과 동일하게 잠금. */}
                    <input
                      type="text"
                      value={subcategory}
                      readOnly
                      placeholder="자료 불러오기 시 자동 채워집니다."
                      className={styles.loadedData}
                    />
                  </div>
                </div>

                {/* 작성일 + 청구일 - 한 줄 배치 */}
                <div className={styles.rowDouble}>
                  <div className={styles.rowLabel}>작성일</div>
                  <div className={styles.rowField}>
                    <input 
                      type="text" 
                      value={formatDateShort(draftDate)} 
                      readOnly
                      className={styles.loadedData}
                    />
                  </div>
                  
                  <div className={styles.rowLabel}>청구일</div>
                  <div className={styles.rowField}>
                    <input 
                      type="text" 
                      value={formatDateShort(claimDate)} 
                      readOnly
                      className={styles.loadedData}
                    />
                  </div>
                </div>

                {/* 작업월 - 가로 배치 */}
                {/* ✅ 자료 불러오기 시 원본의 work_month로만 채워지는 자동 필드 — subcategory와 동일한 사유로 잠금 */}
                <Row label="작업월(귀속월)" inline>
                  <input
                    type="month"
                    value={workMonth}
                    readOnly
                    className={styles.loadedData}
                  />
                </Row>

                {/* 거래처 - 가로 배치 */}
                <Row label="거래처" inline>
                  <input
                    placeholder="직접 입력 또는 검색"
                    value={vendorInput}
                    onChange={handleChangeVendor}
                    onKeyDown={handleVendorKeyDown}
                    readOnly={isDataLoaded}
                    className={isDataLoaded ? styles.loadedData : ""}
                  />
                  {showVendorList && (
                    <div className={styles.partnerBox} ref={vendorListRef}>
                      {filteredVendors.length > 0 ? (
                        filteredVendors.map((v, i) => (
                          <div
                            key={v.id}
                            className={`${styles.partnerItem} ${i === highlightIndex ? styles.activeItem : ""}`}
                            onClick={() => handlePickVendor(v)}
                          >
                            {v.name}
                          </div>
                        ))
                      ) : (
                        <div>일치하는 거래처가 없습니다.</div>
                      )}
                    </div>
                  )}
                </Row>

                {/* 금액 + 거래방법 - 한 줄 배치 */}
                <div className={styles.rowDouble}>
                  <div className={styles.rowLabel}>금액</div>
                  <div className={styles.rowField}>
                    {/* ✅ 자동합계 템플릿(AUTO_SUM_TEMPLATES)은 표(rows)가 금액의 유일한 진실이라
                        직접 입력을 아예 막는다(항상 readOnly) — 자료 불러오기 여부와 무관.
                        비자동합계 템플릿은 기존 그대로: 자료를 불러왔을 때만 잠금. */}
                    <input
                      inputMode="numeric"
                      placeholder="숫자만"
                      value={amount ? Number(amount).toLocaleString() : ""}
                      onChange={(e) => {
                        if (AUTO_SUM_TEMPLATES.includes(detailOption) || isDataLoaded) return;
                        setAmount(e.target.value.replace(/[^\d]/g, ""));
                        setTouchedFields((prev) => ({ ...prev, amount: true }));
                      }}
                      readOnly={AUTO_SUM_TEMPLATES.includes(detailOption) || isDataLoaded}
                      className={(AUTO_SUM_TEMPLATES.includes(detailOption) || isDataLoaded) ? styles.loadedData : ""}
                    />
                  </div>
                  
                  <div className={styles.rowLabel}>거래방법</div>
                  <div className={styles.rowField}>
                    <input 
                      type="text" 
                      value={payMethodLabel} 
                      readOnly
                      className={styles.loadedData}
                    />
                  </div>
                </div>

                {/* ✅ 물품구매/소모품구매 전용 - 품목명 (데이터에서 로드됨) */}
                {(detailOption === "general_purchase" || detailOption === "supplies" || detailOption === "supplies_office" || detailOption === "service_purchase") && (
                  <Row label="품목명">
                    <input
                      type="text"
                      value={serviceContent}
                      onChange={(e) => setServiceContent(e.target.value)}
                      placeholder="예: 친환경 복사용지(A4)"
                      className={serviceContent ? styles.loadedData : ""}
                    />
                  </Row>
                )}

                {/* ✅ 소모품구매 전용 - 결제방식 (pay_method로 자동 판별, 틀렸을 때만 수동 오버라이드) */}
                {detailOption === "supplies" && (
                  <Row label="결제방식">
                    <div className={styles.payTypeRadioGroup}>
                      <label className={styles.payTypeRadioLabel}>
                        <input
                          type="radio"
                          name="supplyPayType"
                          value="card"
                          checked={templateState.supplyPayType === "card"}
                          onChange={(e) => setTemplateField("supplyPayType", e.target.value)}
                        />
                        신용카드결제
                      </label>
                      <label className={styles.payTypeRadioLabel}>
                        <input
                          type="radio"
                          name="supplyPayType"
                          value="transfer"
                          checked={templateState.supplyPayType === "transfer"}
                          onChange={(e) => setTemplateField("supplyPayType", e.target.value)}
                        />
                        계좌이체
                      </label>
                    </div>
                  </Row>
                )}

                {/* ✅ 우편발송 전용 - 발송내용 (품목명과 같은 serviceContent 재사용, 별도 라벨) */}
                {detailOption === "postage" && (
                  <Row label="발송내용">
                    <input
                      type="text"
                      value={serviceContent}
                      onChange={(e) => setServiceContent(e.target.value)}
                      placeholder="예: 계약서 원본"
                      className={serviceContent ? styles.loadedData : ""}
                    />
                  </Row>
                )}

                {/* ✅ 신용카드 결제대금 납부 전용 - 납부월(연+월, 도입문/제목 전용) */}
                {detailOption === "credit_card" && (
                  <YearMonthFields
                    label="납부월"
                    value={templateState.creditCardMonth}
                    onChange={(v) => setTemplateField("creditCardMonth", v)}
                  />
                )}

                {/* ✅ 기장수수료 전용 - 집행월(연+월, 도입문/제목 전용) */}
                {detailOption === "accounting_fee" && (
                  <YearMonthFields
                    label="집행월"
                    value={templateState.accountingMonth}
                    onChange={(v) => setTemplateField("accountingMonth", v)}
                  />
                )}

                {/* ✅ 인터넷·전화요금 전용 - 납부월(연+월) + 납기일(연+월+일). EXPENSE_TEMPLATES에
                    정의된 "dueDate"는 PDF 미리보기 라벨("4. 납 기 일")용 참조일 뿐 — 실제 값은
                    templateState.utilityFeeDueDateRaw(저장/복원됨)에서 온다. */}
                {detailOption === "utility_fee" && (
                  <>
                    <YearMonthFields
                      label="납부월"
                      value={templateState.utilityMonth}
                      onChange={(v) => setTemplateField("utilityMonth", v)}
                    />
                    <DueDateFields
                      label="납기일"
                      value={templateState.utilityFeeDueDateRaw}
                      onChange={(v) => setTemplateField("utilityFeeDueDateRaw", v)}
                    />
                  </>
                )}

                {/* ✅ 주차비용 전용 - 지점 + 발생사유 (도입문 전용, PDF 번호 매김 필드 아님) */}
                {detailOption === "parking_fee" && (
                  <>
                    <Row label="지점">
                      <input
                        type="text"
                        value={templateState.parkingBranch}
                        onChange={(e) => setTemplateField("parkingBranch", e.target.value)}
                        placeholder="예: 가산점"
                        className={templateState.parkingBranch ? styles.loadedData : ""}
                      />
                    </Row>
                    <Row label="발생사유">
                      <input
                        type="text"
                        value={templateState.parkingReason}
                        onChange={(e) => setTemplateField("parkingReason", e.target.value)}
                        placeholder="예: 통장을 개설함"
                        className={templateState.parkingReason ? styles.loadedData : ""}
                      />
                    </Row>
                  </>
                )}

                {/* ✅ 법인인감증명서 전용 - 지출사유 (도입문 전용, PDF 번호 매김 필드 아님) */}
                {detailOption === "corporate_seal" && (
                  <Row label="지출사유">
                    <input
                      type="text"
                      value={templateState.sealReason}
                      onChange={(e) => setTemplateField("sealReason", e.target.value)}
                      placeholder="예: 은행 계좌 개설"
                      className={templateState.sealReason ? styles.loadedData : ""}
                    />
                  </Row>
                )}

                {/* ✅ 사업자등록증/영업신고수수료 전용 - 발급대상 (도입문/건명 전용, PDF 번호 매김 필드 아님) */}
                {(detailOption === "biz_license" || detailOption === "biz_report") && (
                  <Row label="발급대상">
                    <input
                      type="text"
                      value={templateState.bizTarget}
                      onChange={(e) => setTemplateField("bizTarget", e.target.value)}
                      placeholder="예: 신규 카페인 그린점"
                      className={templateState.bizTarget ? styles.loadedData : ""}
                    />
                  </Row>
                )}

                {/* ✅ 영업신고수수료 전용 - 업종 (도입문 전용, PDF 번호 매김 필드 아님) */}
                {detailOption === "biz_report" && (
                  <Row label="업종">
                    <input
                      type="text"
                      value={templateState.bizCategory}
                      onChange={(e) => setTemplateField("bizCategory", e.target.value)}
                      placeholder="예: 식품접객업"
                      className={templateState.bizCategory ? styles.loadedData : ""}
                    />
                  </Row>
                )}

                {/* ✅ 임직원급여/카페인 가산점 급여지급 전용 - 지급일 (PDF 번호 매김 필드, 값은 formatPayDate로 자동 포맷) */}
                {(detailOption === "payroll" || detailOption === "gasan_payroll" || detailOption === "doksan_payroll" || detailOption === "geumnarae_payroll" || detailOption === "green_payroll") && (
                  <Row label="지급일">
                    <input
                      type="date"
                      value={templateState.payDateRaw}
                      onChange={(e) => setTemplateField("payDateRaw", e.target.value)}
                      className={templateState.payDateRaw ? styles.loadedData : ""}
                    />
                  </Row>
                )}

                {/* ✅ 사무실관리비 전용 - 청구월(수정 가능, 연도 없는 "M월" 원본 표기라 월 칸만) + 납기일 (PDF 번호 매김 필드 아님/필드) */}
                {detailOption === "office_maintenance" && (
                  <>
                    <YearMonthFields
                      label="청구월"
                      value={templateState.maintMonth}
                      onChange={(v) => setTemplateField("maintMonth", v)}
                      showYear={false}
                    />
                    <Row label="납기일">
                      <input
                        type="date"
                        value={templateState.maintDueDateRaw}
                        onChange={(e) => setTemplateField("maintDueDateRaw", e.target.value)}
                        className={templateState.maintDueDateRaw ? styles.loadedData : ""}
                      />
                    </Row>
                  </>
                )}

                {/* ✅ 카페인 가산점 관리비 전용 - 납기일 (귀속연월은 GASAN_MONTH_TEMPLATES 공용 Row로 처리) */}
                {(detailOption === "gasan_maintenance" || detailOption === "green_maintenance") && (
                  <Row label="납기일">
                    <input
                      type="date"
                      value={templateState.maintDueDateRaw}
                      onChange={(e) => setTemplateField("maintDueDateRaw", e.target.value)}
                      className={templateState.maintDueDateRaw ? styles.loadedData : ""}
                    />
                  </Row>
                )}

                {/* ✅ 카페인 가산점/독산점/금나래점 신용카드결제 전용 - 카드번호 + 연회비 + 비고 (귀속연월은 공용 Row로 처리,
                    카드번호·비고 기본값은 detailOption별로 다르므로 useEffect(가맹점별 기본값)로 구분) */}
                {(detailOption === "gasan_credit_card" || detailOption === "doksan_credit_card" || detailOption === "geumnarae_credit_card" || detailOption === "green_credit_card") && (
                  <>
                    <Row label="카드번호">
                      <input
                        type="text"
                        value={templateState.gasanCardNo}
                        onChange={(e) => {
                          setTemplateField("gasanCardNo", e.target.value);
                          setTouchedFields((prev) => ({ ...prev, gasanCardNo: true }));
                        }}
                        placeholder="예: 4261, 0887"
                        className={templateState.gasanCardNo ? styles.loadedData : ""}
                      />
                    </Row>
                    <Row label="연회비">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={templateState.gasanCardFee ? Number(templateState.gasanCardFee).toLocaleString() : ""}
                        onChange={(e) => setTemplateField("gasanCardFee", e.target.value.replace(/[^\d]/g, ""))}
                        className={templateState.gasanCardFee ? styles.loadedData : ""}
                      />
                    </Row>
                    <Row label="비고">
                      <input
                        type="text"
                        value={templateState.gasanCardNote}
                        onChange={(e) => {
                          setTemplateField("gasanCardNote", e.target.value);
                          setTouchedFields((prev) => ({ ...prev, gasanCardNote: true }));
                        }}
                        className={templateState.gasanCardNote ? styles.loadedData : ""}
                      />
                    </Row>
                  </>
                )}

                {/* ✅ 카페인 가산점/독산점 사회보험료 전용 - 직원별 본인/기관부담금 가변 행(state 공유)
                    (원본 인건비 내역 자동 채움 대상이라 다른 가변 행들과 달리 touchedFields 보호 필요 —
                    보호 없으면 템플릿을 벗어났다 되돌아올 때 자동 채움 효과가 사용자 수정을 덮어씀) */}
                {(detailOption === "gasan_social_insurance" || detailOption === "doksan_social_insurance" || detailOption === "geumnarae_social_insurance" || detailOption === "green_social_insurance") && (
                  <>
                    {templateState.socialInsuranceRows.map((row, i) => (
                      <Row label={`직원 ${i + 1}`} key={i}>
                        <div className={styles.refundRowGroup}>
                          <input
                            type="text"
                            value={row.name}
                            onChange={(e) => {
                              const next = [...templateState.socialInsuranceRows];
                              next[i] = { ...next[i], name: e.target.value };
                              setTemplateField("socialInsuranceRows", next);
                              setTouchedFields((prev) => ({ ...prev, socialInsuranceRows: true }));
                            }}
                            placeholder="성명"
                          />
                          <input
                            type="text" inputMode="numeric"
                            value={row.health_self}
                            onChange={(e) => {
                              const next = [...templateState.socialInsuranceRows];
                              next[i] = { ...next[i], health_self: e.target.value.replace(/[^\d]/g, "") };
                              setTemplateField("socialInsuranceRows", next);
                              setTouchedFields((prev) => ({ ...prev, socialInsuranceRows: true }));
                            }}
                            placeholder="건강보험(본인)"
                          />
                          <input
                            type="text" inputMode="numeric"
                            value={row.health_org}
                            onChange={(e) => {
                              const next = [...templateState.socialInsuranceRows];
                              next[i] = { ...next[i], health_org: e.target.value.replace(/[^\d]/g, "") };
                              setTemplateField("socialInsuranceRows", next);
                              setTouchedFields((prev) => ({ ...prev, socialInsuranceRows: true }));
                            }}
                            placeholder="건강보험(기관)"
                          />
                          <input
                            type="text" inputMode="numeric"
                            value={row.care_self}
                            onChange={(e) => {
                              const next = [...templateState.socialInsuranceRows];
                              next[i] = { ...next[i], care_self: e.target.value.replace(/[^\d]/g, "") };
                              setTemplateField("socialInsuranceRows", next);
                              setTouchedFields((prev) => ({ ...prev, socialInsuranceRows: true }));
                            }}
                            placeholder="장기요양(본인)"
                          />
                          <input
                            type="text" inputMode="numeric"
                            value={row.care_org}
                            onChange={(e) => {
                              const next = [...templateState.socialInsuranceRows];
                              next[i] = { ...next[i], care_org: e.target.value.replace(/[^\d]/g, "") };
                              setTemplateField("socialInsuranceRows", next);
                              setTouchedFields((prev) => ({ ...prev, socialInsuranceRows: true }));
                            }}
                            placeholder="장기요양(기관)"
                          />
                          <input
                            type="text" inputMode="numeric"
                            value={row.pension_self}
                            onChange={(e) => {
                              const next = [...templateState.socialInsuranceRows];
                              next[i] = { ...next[i], pension_self: e.target.value.replace(/[^\d]/g, "") };
                              setTemplateField("socialInsuranceRows", next);
                              setTouchedFields((prev) => ({ ...prev, socialInsuranceRows: true }));
                            }}
                            placeholder="국민연금(본인)"
                          />
                          <input
                            type="text" inputMode="numeric"
                            value={row.pension_org}
                            onChange={(e) => {
                              const next = [...templateState.socialInsuranceRows];
                              next[i] = { ...next[i], pension_org: e.target.value.replace(/[^\d]/g, "") };
                              setTemplateField("socialInsuranceRows", next);
                              setTouchedFields((prev) => ({ ...prev, socialInsuranceRows: true }));
                            }}
                            placeholder="국민연금(기관)"
                          />
                          <input
                            type="text" inputMode="numeric"
                            value={row.employment_self}
                            onChange={(e) => {
                              const next = [...templateState.socialInsuranceRows];
                              next[i] = { ...next[i], employment_self: e.target.value.replace(/[^\d]/g, "") };
                              setTemplateField("socialInsuranceRows", next);
                              setTouchedFields((prev) => ({ ...prev, socialInsuranceRows: true }));
                            }}
                            placeholder="고용보험(본인)"
                          />
                          <input
                            type="text" inputMode="numeric"
                            value={row.employment_org}
                            onChange={(e) => {
                              const next = [...templateState.socialInsuranceRows];
                              next[i] = { ...next[i], employment_org: e.target.value.replace(/[^\d]/g, "") };
                              setTemplateField("socialInsuranceRows", next);
                              setTouchedFields((prev) => ({ ...prev, socialInsuranceRows: true }));
                            }}
                            placeholder="고용보험(기관)"
                          />
                          <input
                            type="text" inputMode="numeric"
                            value={row.industrial_org}
                            onChange={(e) => {
                              const next = [...templateState.socialInsuranceRows];
                              next[i] = { ...next[i], industrial_org: e.target.value.replace(/[^\d]/g, "") };
                              setTemplateField("socialInsuranceRows", next);
                              setTouchedFields((prev) => ({ ...prev, socialInsuranceRows: true }));
                            }}
                            placeholder="산재보험(기관)"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setTemplateField(
                                "socialInsuranceRows",
                                templateState.socialInsuranceRows.filter((_, ri) => ri !== i)
                              );
                              setTouchedFields((prev) => ({ ...prev, socialInsuranceRows: true }));
                            }}
                            disabled={templateState.socialInsuranceRows.length <= 1}
                          >
                            ✕
                          </button>
                        </div>
                      </Row>
                    ))}
                    <Row label="">
                      <button
                        type="button"
                        className={styles.refundAddBtn}
                        onClick={() => {
                          setTemplateField("socialInsuranceRows", [
                            ...templateState.socialInsuranceRows,
                            defaultSocialInsuranceRows()[0],
                          ]);
                          setTouchedFields((prev) => ({ ...prev, socialInsuranceRows: true }));
                        }}
                      >
                        + 직원 추가
                      </button>
                    </Row>
                  </>
                )}

                {/* ✅ 기부금 전용 - 기부사유 + 기부처 (도입문 전용, PDF 번호 매김 필드 아님) */}
                {detailOption === "donation" && (
                  <>
                    <Row label="기부사유">
                      <input
                        type="text"
                        value={templateState.donationReason}
                        onChange={(e) => setTemplateField("donationReason", e.target.value)}
                        placeholder="예: 독립유공자의 희생과 공헌을 기리기 위해"
                        className={templateState.donationReason ? styles.loadedData : ""}
                      />
                    </Row>
                    <Row label="기부처">
                      <input
                        type="text"
                        value={templateState.donationTarget}
                        onChange={(e) => setTemplateField("donationTarget", e.target.value)}
                        placeholder="예: 사단법인 독립유공자유족회"
                        className={templateState.donationTarget ? styles.loadedData : ""}
                      />
                    </Row>
                  </>
                )}

                {/* ✅ 복지포인트 전용 - 신청인 + 신청사유 (도입문 전용, PDF 번호 매김 필드 아님) */}
                {detailOption === "welfare_points" && (
                  <>
                    <Row label="신청인">
                      <input
                        type="text"
                        value={templateState.welfareApplicant}
                        onChange={(e) => {
                          setTemplateField("welfareApplicant", e.target.value);
                          setTouchedFields((prev) => ({ ...prev, welfareApplicant: true }));
                        }}
                        placeholder="예: 서복성"
                        className={templateState.welfareApplicant ? styles.loadedData : ""}
                      />
                    </Row>
                    <Row label="신청사유">
                      <select
                        value={templateState.welfareReason}
                        onChange={(e) => setTemplateField("welfareReason", e.target.value)}
                        className={templateState.welfareReason ? styles.loadedData : ""}
                      >
                        <option value="">선택하세요</option>
                        <option value="건강관리">건강관리</option>
                        <option value="자기계발">자기계발</option>
                        <option value="여가활동">여가활동</option>
                        <option value="가족친화">가족친화</option>
                      </select>
                    </Row>
                  </>
                )}

                {/* ✅ 용역계약 전용 - 계약명(건명 겸용) + 계약금액 + 용역기간(시작/종료) */}
                {detailOption === "service_contract" && (
                  <>
                    <Row label="계약명">
                      <input
                        type="text"
                        value={templateState.contractName}
                        onChange={(e) => setTemplateField("contractName", e.target.value)}
                        placeholder="예: 2026년 청소용역 계약"
                        className={templateState.contractName ? styles.loadedData : ""}
                      />
                    </Row>
                    <Row label="계약금액">
                      <input
                        inputMode="numeric"
                        placeholder="숫자만"
                        value={templateState.contractAmountRaw ? Number(templateState.contractAmountRaw).toLocaleString() : ""}
                        onChange={(e) => setTemplateField("contractAmountRaw", e.target.value.replace(/[^\d]/g, ""))}
                        className={templateState.contractAmountRaw ? styles.loadedData : ""}
                      />
                    </Row>
                    <Row label="용역기간(시작)">
                      <input
                        type="date"
                        value={templateState.contractStartRaw}
                        onChange={(e) => setTemplateField("contractStartRaw", e.target.value)}
                        className={templateState.contractStartRaw ? styles.loadedData : ""}
                      />
                    </Row>
                    <Row label="용역기간(종료)">
                      <input
                        type="date"
                        value={templateState.contractEndRaw}
                        onChange={(e) => setTemplateField("contractEndRaw", e.target.value)}
                        className={templateState.contractEndRaw ? styles.loadedData : ""}
                      />
                    </Row>
                  </>
                )}

                {/* ✅ 직무교육비 전용 - 수강자 + 교육과정 + 교육일정 + 대상자 + 교육기관 */}
                {detailOption === "job_training" && (
                  <>
                    <Row label="수강자">
                      <input
                        type="text"
                        value={templateState.trainee}
                        onChange={(e) => setTemplateField("trainee", e.target.value)}
                        placeholder="예: 사무직원(윤길상)"
                        className={templateState.trainee ? styles.loadedData : ""}
                      />
                    </Row>
                    <Row label="교육과정">
                      <input
                        type="text"
                        value={templateState.trainCourse}
                        onChange={(e) => setTemplateField("trainCourse", e.target.value)}
                        placeholder="예: 효과적인 사업계획서 작성법(1기)"
                        className={templateState.trainCourse ? styles.loadedData : ""}
                      />
                    </Row>
                    <Row label="교육일정">
                      <input
                        type="text"
                        value={templateState.trainSchedule}
                        onChange={(e) => setTemplateField("trainSchedule", e.target.value)}
                        placeholder="예: 2026. 6. 12. (1일, 7시간)"
                        className={templateState.trainSchedule ? styles.loadedData : ""}
                      />
                    </Row>
                    <Row label="대상자">
                      <input
                        type="text"
                        value={templateState.trainTarget}
                        onChange={(e) => setTemplateField("trainTarget", e.target.value)}
                        placeholder="예: 사업관리팀 윤길상 팀장"
                        className={templateState.trainTarget ? styles.loadedData : ""}
                      />
                    </Row>
                    <Row label="교육기관">
                      <input
                        type="text"
                        value={templateState.trainInstitution}
                        onChange={(e) => setTemplateField("trainInstitution", e.target.value)}
                        placeholder="예: 지방공기업평가원"
                        className={templateState.trainInstitution ? styles.loadedData : ""}
                      />
                    </Row>
                  </>
                )}

                {/* ✅ 배너광고 전용 - 광고대상 (도입문/건명 전용, PDF 번호 매김 필드 아님) */}
                {detailOption === "banner_ad" && (
                  <Row label="광고대상">
                    <input
                      type="text"
                      value={templateState.adTarget}
                      onChange={(e) => setTemplateField("adTarget", e.target.value)}
                      placeholder="예: 카페사업"
                      className={templateState.adTarget ? styles.loadedData : ""}
                    />
                  </Row>
                )}

                {/* ✅ 전기료 전용 - 청구월(수정 가능, 연도 없는 "M월" 원본 표기라 월 칸만) + 사용기간(시작/종료) + 납기일 */}
                {detailOption === "electric_bill" && (
                  <>
                    <YearMonthFields
                      label="청구월"
                      value={templateState.elecMonth}
                      onChange={(v) => setTemplateField("elecMonth", v)}
                      showYear={false}
                    />
                    <Row label="사용기간(시작)">
                      <input
                        type="date"
                        value={templateState.elecUsageStartRaw}
                        onChange={(e) => setTemplateField("elecUsageStartRaw", e.target.value)}
                        className={templateState.elecUsageStartRaw ? styles.loadedData : ""}
                      />
                    </Row>
                    <Row label="사용기간(종료)">
                      <input
                        type="date"
                        value={templateState.elecUsageEndRaw}
                        onChange={(e) => setTemplateField("elecUsageEndRaw", e.target.value)}
                        className={templateState.elecUsageEndRaw ? styles.loadedData : ""}
                      />
                    </Row>
                    <Row label="납기일">
                      <input
                        type="date"
                        value={templateState.elecDueDateRaw}
                        onChange={(e) => setTemplateField("elecDueDateRaw", e.target.value)}
                        className={templateState.elecDueDateRaw ? styles.loadedData : ""}
                      />
                    </Row>
                  </>
                )}

                {/* ✅ 유류비 전용 - 차량번호 + 차종 (환경/종합은 포터 1대뿐이라 기본값 자동채움, 경영팀/용역은 수동입력) */}
                {detailOption === "fuel_cost" && (
                  <>
                    <Row label="차량번호">
                      <input
                        type="text"
                        value={templateState.carNumber}
                        onChange={(e) => {
                          setTemplateField("carNumber", e.target.value);
                          setTouchedFields((prev) => ({ ...prev, carNumber: true }));
                        }}
                        placeholder="예: 104머9770"
                        className={templateState.carNumber ? styles.loadedData : ""}
                      />
                    </Row>
                    <Row label="차종">
                      <input
                        type="text"
                        value={templateState.carModel}
                        onChange={(e) => {
                          setTemplateField("carModel", e.target.value);
                          setTouchedFields((prev) => ({ ...prev, carModel: true }));
                        }}
                        placeholder="예: 코란도"
                        className={templateState.carModel ? styles.loadedData : ""}
                      />
                    </Row>
                  </>
                )}

                {/* ✅ 차량리스료 전용 - 수납회차 (나머지 계약정보는 전부 고정값, 잔여회차는 60-수납회차 자동계산) */}
                {detailOption === "env_total_car_lease" && (
                  <Row label="수납회차">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={templateState.leaseInstallmentNo}
                      onChange={(e) => setTemplateField("leaseInstallmentNo", e.target.value.replace(/[^\d]/g, ""))}
                      placeholder="예: 15"
                      className={templateState.leaseInstallmentNo ? styles.loadedData : ""}
                    />
                  </Row>
                )}

                {/* ✅ 카페인 가산점 전용 - 귀속연월 (포스임대료 제외, 나머지 필드는 templateInputs 범용 메커니즘으로 자동 처리) */}
                {GASAN_MONTH_TEMPLATES.includes(detailOption) && (
                  <Row label="귀속연월">
                    <input
                      type="text"
                      value={templateState.gasanMonth}
                      onChange={(e) => {
                        setTemplateField("gasanMonth", e.target.value);
                        setTouchedFields((prev) => ({ ...prev, gasanMonth: true }));
                      }}
                      placeholder="예: 2026년 4월"
                      className={templateState.gasanMonth ? styles.loadedData : ""}
                    />
                  </Row>
                )}

                {/* ✅ 채용심사비 전용 - 서류심사/면접심사 단가×회수×인원 (표 세부내역 자동계산) */}
                {detailOption === "interview_fee" && (
                  <>
                    <Row label="서류심사">
                      <div className={styles.interviewFeeRow}>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={templateState.docFee ? Number(templateState.docFee).toLocaleString() : ""}
                          onChange={(e) => setTemplateField("docFee", e.target.value.replace(/[^\d]/g, ""))}
                          placeholder="단가"
                        />
                        <span>원 ×</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={templateState.docCount}
                          onChange={(e) => setTemplateField("docCount", e.target.value.replace(/[^\d]/g, ""))}
                          placeholder="회수"
                        />
                        <span>회 ×</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={templateState.docPersons}
                          onChange={(e) => setTemplateField("docPersons", e.target.value.replace(/[^\d]/g, ""))}
                          placeholder="인원"
                        />
                        <span>인</span>
                      </div>
                    </Row>
                    <Row label="면접심사">
                      <div className={styles.interviewFeeRow}>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={templateState.intFee ? Number(templateState.intFee).toLocaleString() : ""}
                          onChange={(e) => setTemplateField("intFee", e.target.value.replace(/[^\d]/g, ""))}
                          placeholder="단가"
                        />
                        <span>원 ×</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={templateState.intCount}
                          onChange={(e) => setTemplateField("intCount", e.target.value.replace(/[^\d]/g, ""))}
                          placeholder="회수"
                        />
                        <span>회 ×</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={templateState.intPersons}
                          onChange={(e) => setTemplateField("intPersons", e.target.value.replace(/[^\d]/g, ""))}
                          placeholder="인원"
                        />
                        <span>인</span>
                      </div>
                    </Row>
                  </>
                )}

                {/* ✅ 차량할부금 전용 - 회차 (매달 반복되는 문서라 이것만 바뀜) */}
                {detailOption === "car_installment" && (
                  <Row label="회차">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={templateState.installmentRound}
                      onChange={(e) => setTemplateField("installmentRound", e.target.value.replace(/[^\d]/g, ""))}
                      placeholder="예: 15"
                      className={templateState.installmentRound ? styles.loadedData : ""}
                    />
                  </Row>
                )}

                {/* ✅ 공제금환급 전용 - 대상자 가변 행(성명/금액) + 공통 비고 */}
                {detailOption === "deduction_refund" && (
                  <>
                    {templateState.refundRows.map((row, i) => (
                      <Row label={`대상자 ${i + 1}`} key={i}>
                        <div className={styles.refundRowGroup}>
                          <input
                            type="text"
                            value={row.name}
                            onChange={(e) => {
                              const next = [...templateState.refundRows];
                              next[i] = { ...next[i], name: e.target.value };
                              setTemplateField("refundRows", next);
                            }}
                            placeholder="성명"
                          />
                          <input
                            type="text"
                            inputMode="numeric"
                            value={row.amount ? Number(row.amount).toLocaleString() : ""}
                            onChange={(e) => {
                              const next = [...templateState.refundRows];
                              next[i] = { ...next[i], amount: e.target.value.replace(/[^\d]/g, "") };
                              setTemplateField("refundRows", next);
                            }}
                            placeholder="금액"
                          />
                          <button
                            type="button"
                            onClick={() => setTemplateField("refundRows", templateState.refundRows.filter((_, ri) => ri !== i))}
                            disabled={templateState.refundRows.length <= 1}
                          >
                            ✕
                          </button>
                        </div>
                      </Row>
                    ))}
                    <Row label="">
                      <button
                        type="button"
                        className={styles.refundAddBtn}
                        onClick={() => setTemplateField("refundRows", [...templateState.refundRows, { name: "", amount: "" }])}
                      >
                        + 대상자 추가
                      </button>
                    </Row>
                    <Row label="비고">
                      <input
                        type="text"
                        value={templateState.refundNote}
                        onChange={(e) => setTemplateField("refundNote", e.target.value)}
                        placeholder="예: 2026년 5월 일용직 공제금 환급"
                        className={templateState.refundNote ? styles.loadedData : ""}
                      />
                    </Row>
                  </>
                )}

                {/* ✅ 퇴직연금 전용 - 청구월(연도 없는 "M월" 원본 표기라 월 칸만) + 납부기한 + 부문별 가변 행 + 사업자수수료 */}
                {detailOption === "pension" && (
                  <>
                    <YearMonthFields
                      label="청구월"
                      value={templateState.pensionMonth}
                      onChange={(v) => setTemplateField("pensionMonth", v)}
                      showYear={false}
                    />
                    <Row label="납부기한">
                      <input
                        type="date"
                        value={templateState.pensionDueRaw}
                        onChange={(e) => setTemplateField("pensionDueRaw", e.target.value)}
                        className={templateState.pensionDueRaw ? styles.loadedData : ""}
                      />
                    </Row>
                    {templateState.pensionRows.map((row, i) => (
                      <Row label={`부문 ${i + 1}`} key={i}>
                        <div className={styles.refundRowGroup}>
                          <input
                            type="text"
                            value={row.dept}
                            onChange={(e) => {
                              const next = [...templateState.pensionRows];
                              next[i] = { ...next[i], dept: e.target.value };
                              setTemplateField("pensionRows", next);
                            }}
                            placeholder="부문"
                          />
                          <input
                            type="text"
                            value={row.name}
                            onChange={(e) => {
                              const next = [...templateState.pensionRows];
                              next[i] = { ...next[i], name: e.target.value };
                              setTemplateField("pensionRows", next);
                            }}
                            placeholder="성명"
                          />
                          <input
                            type="text"
                            inputMode="numeric"
                            value={row.extra}
                            onChange={(e) => {
                              const next = [...templateState.pensionRows];
                              next[i] = { ...next[i], extra: e.target.value.replace(/[^\d]/g, "") };
                              setTemplateField("pensionRows", next);
                            }}
                            placeholder="외 N명"
                          />
                          <input
                            type="text"
                            inputMode="numeric"
                            value={row.amount ? Number(row.amount).toLocaleString() : ""}
                            onChange={(e) => {
                              const next = [...templateState.pensionRows];
                              next[i] = { ...next[i], amount: e.target.value.replace(/[^\d]/g, "") };
                              setTemplateField("pensionRows", next);
                            }}
                            placeholder="금액"
                          />
                          <button
                            type="button"
                            onClick={() => setTemplateField("pensionRows", templateState.pensionRows.filter((_, ri) => ri !== i))}
                            disabled={templateState.pensionRows.length <= 1}
                          >
                            ✕
                          </button>
                        </div>
                      </Row>
                    ))}
                    <Row label="">
                      <button
                        type="button"
                        className={styles.refundAddBtn}
                        onClick={() =>
                          setTemplateField("pensionRows", [...templateState.pensionRows, { dept: "", name: "", extra: "", amount: "" }])
                        }
                      >
                        + 부문 추가
                      </button>
                    </Row>
                    <Row label="사업자수수료">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={templateState.pensionFeeAmount ? Number(templateState.pensionFeeAmount).toLocaleString() : ""}
                        onChange={(e) => setTemplateField("pensionFeeAmount", e.target.value.replace(/[^\d]/g, ""))}
                        placeholder="금액"
                        className={templateState.pensionFeeAmount ? styles.loadedData : ""}
                      />
                    </Row>
                  </>
                )}

                {/* ✅ 원천세 전용(경영팀/카페인 독산점 공용) - 가변 행(내역/성명/외N명/근로소득세/지방소득세) + 납부기한 */}
                {(detailOption === "withholding_tax" || detailOption === "doksan_withholding_tax" || detailOption === "geumnarae_withholding_tax" || detailOption === "green_withholding_tax") && (
                  <>
                    {templateState.withholdingRows.map((row, i) => (
                      <Row label={`징수분 ${i + 1}`} key={i}>
                        <div className={styles.refundRowGroup}>
                          <input
                            type="text"
                            value={row.desc}
                            onChange={(e) => {
                              const next = [...templateState.withholdingRows];
                              next[i] = { ...next[i], desc: e.target.value };
                              setTemplateField("withholdingRows", next);
                            }}
                            placeholder="내역"
                          />
                          <input
                            type="text"
                            value={row.name}
                            onChange={(e) => {
                              const next = [...templateState.withholdingRows];
                              next[i] = { ...next[i], name: e.target.value };
                              setTemplateField("withholdingRows", next);
                            }}
                            placeholder="성명"
                          />
                          <input
                            type="text"
                            inputMode="numeric"
                            value={row.extra}
                            onChange={(e) => {
                              const next = [...templateState.withholdingRows];
                              next[i] = { ...next[i], extra: e.target.value.replace(/[^\d]/g, "") };
                              setTemplateField("withholdingRows", next);
                            }}
                            placeholder="외 N명"
                          />
                          <input
                            type="text"
                            inputMode="numeric"
                            value={row.laborTax ? Number(row.laborTax).toLocaleString() : ""}
                            onChange={(e) => {
                              const next = [...templateState.withholdingRows];
                              next[i] = { ...next[i], laborTax: e.target.value.replace(/[^\d]/g, "") };
                              setTemplateField("withholdingRows", next);
                            }}
                            placeholder="근로소득세"
                          />
                          <input
                            type="text"
                            inputMode="numeric"
                            value={row.localTax ? Number(row.localTax).toLocaleString() : ""}
                            onChange={(e) => {
                              const next = [...templateState.withholdingRows];
                              next[i] = { ...next[i], localTax: e.target.value.replace(/[^\d]/g, "") };
                              setTemplateField("withholdingRows", next);
                            }}
                            placeholder="지방소득세"
                          />
                          <button
                            type="button"
                            onClick={() => setTemplateField("withholdingRows", templateState.withholdingRows.filter((_, ri) => ri !== i))}
                            disabled={templateState.withholdingRows.length <= 1}
                          >
                            ✕
                          </button>
                        </div>
                      </Row>
                    ))}
                    <Row label="">
                      <button
                        type="button"
                        className={styles.refundAddBtn}
                        onClick={() =>
                          setTemplateField("withholdingRows", [
                            ...templateState.withholdingRows,
                            { desc: "", name: "", extra: "", laborTax: "", localTax: "" },
                          ])
                        }
                      >
                        + 징수분 추가
                      </button>
                    </Row>
                    <Row label="납부기한">
                      <input
                        type="date"
                        value={templateState.withholdingDueRaw}
                        onChange={(e) => setTemplateField("withholdingDueRaw", e.target.value)}
                        className={templateState.withholdingDueRaw ? styles.loadedData : ""}
                      />
                    </Row>
                  </>
                )}

                {/* ✅ 카페인 독산점 명절격려품 전용 - 가변 행(지급대상/상품명/수량/공급가액/부가세) */}
                {(detailOption === "doksan_holiday_gift" || detailOption === "geumnarae_holiday_gift" || detailOption === "green_holiday_gift") && (
                  <>
                    {templateState.holidayGiftRows.map((row, i) => (
                      <Row label={`지급 ${i + 1}`} key={i}>
                        <div className={styles.refundRowGroup}>
                          <input
                            type="text"
                            value={row.target}
                            onChange={(e) => {
                              const next = [...templateState.holidayGiftRows];
                              next[i] = { ...next[i], target: e.target.value };
                              setTemplateField("holidayGiftRows", next);
                            }}
                            placeholder="지급대상"
                          />
                          <input
                            type="text"
                            value={row.name}
                            onChange={(e) => {
                              const next = [...templateState.holidayGiftRows];
                              next[i] = { ...next[i], name: e.target.value };
                              setTemplateField("holidayGiftRows", next);
                            }}
                            placeholder="상품명"
                          />
                          <input
                            type="text"
                            inputMode="numeric"
                            value={row.qty}
                            onChange={(e) => {
                              const next = [...templateState.holidayGiftRows];
                              next[i] = { ...next[i], qty: e.target.value.replace(/[^\d]/g, "") };
                              setTemplateField("holidayGiftRows", next);
                            }}
                            placeholder="수량"
                          />
                          <input
                            type="text"
                            inputMode="numeric"
                            value={row.supply ? Number(row.supply).toLocaleString() : ""}
                            onChange={(e) => {
                              const next = [...templateState.holidayGiftRows];
                              next[i] = { ...next[i], supply: e.target.value.replace(/[^\d]/g, "") };
                              setTemplateField("holidayGiftRows", next);
                            }}
                            placeholder="공급가액"
                          />
                          <input
                            type="text"
                            inputMode="numeric"
                            value={row.vat ? Number(row.vat).toLocaleString() : ""}
                            onChange={(e) => {
                              const next = [...templateState.holidayGiftRows];
                              next[i] = { ...next[i], vat: e.target.value.replace(/[^\d]/g, "") };
                              setTemplateField("holidayGiftRows", next);
                            }}
                            placeholder="부가세"
                          />
                          <button
                            type="button"
                            onClick={() => setTemplateField("holidayGiftRows", templateState.holidayGiftRows.filter((_, ri) => ri !== i))}
                            disabled={templateState.holidayGiftRows.length <= 1}
                          >
                            ✕
                          </button>
                        </div>
                      </Row>
                    ))}
                    <Row label="">
                      <button
                        type="button"
                        className={styles.refundAddBtn}
                        onClick={() =>
                          setTemplateField("holidayGiftRows", [
                            ...templateState.holidayGiftRows,
                            { target: "", name: "", qty: "", supply: "", vat: "" },
                          ])
                        }
                      >
                        + 지급대상 추가
                      </button>
                    </Row>
                  </>
                )}

                {/* ✅ 카페인 독산점 자판기단말기 전용 - 수량×단가 (공급가액/부가세/합계는 자동계산) */}
                {(detailOption === "doksan_vending_terminal" || detailOption === "green_vending_terminal") && (
                  <>
                    <Row label="수량(대)">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={templateState.vendingQty}
                        onChange={(e) => setTemplateField("vendingQty", e.target.value.replace(/[^\d]/g, ""))}
                        className={templateState.vendingQty ? styles.loadedData : ""}
                      />
                    </Row>
                    <Row label="단가">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={templateState.vendingUnitPrice ? Number(templateState.vendingUnitPrice).toLocaleString() : ""}
                        onChange={(e) => setTemplateField("vendingUnitPrice", e.target.value.replace(/[^\d]/g, ""))}
                        className={templateState.vendingUnitPrice ? styles.loadedData : ""}
                      />
                    </Row>
                  </>
                )}

                {/* ✅ 카페인 독산점/금나래점 부가세 전용 - 연도 + 기수 + (금나래만)구분 + 납기일.
                    독산은 "예정고지" 고정 문구라 구분 입력이 없고, 금나래는 확정 부가세도 있어
                    vatType을 별도 입력받음 */}
                {(detailOption === "doksan_vat" || detailOption === "geumnarae_vat" || detailOption === "green_vat") && (
                  <>
                    <Row label="연도">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={templateState.vatYear}
                        onChange={(e) => setTemplateField("vatYear", e.target.value.replace(/[^\d]/g, ""))}
                        placeholder="예: 2026"
                        className={templateState.vatYear ? styles.loadedData : ""}
                      />
                    </Row>
                    <Row label="기수">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={templateState.vatQuarter}
                        onChange={(e) => setTemplateField("vatQuarter", e.target.value.replace(/[^\d]/g, ""))}
                        placeholder="예: 1"
                        className={templateState.vatQuarter ? styles.loadedData : ""}
                      />
                    </Row>
                    {(detailOption === "geumnarae_vat" || detailOption === "green_vat") && (
                      <Row label="구분(예정고지/확정)">
                        <input
                          type="text"
                          value={templateState.vatType}
                          onChange={(e) => setTemplateField("vatType", e.target.value)}
                          placeholder="예: 예정고지, 확정"
                          className={templateState.vatType ? styles.loadedData : ""}
                        />
                      </Row>
                    )}
                    <Row label="납기일">
                      <input
                        type="date"
                        value={templateState.vatDueRaw}
                        onChange={(e) => setTemplateField("vatDueRaw", e.target.value)}
                        className={templateState.vatDueRaw ? styles.loadedData : ""}
                      />
                    </Row>
                  </>
                )}

                {/* ✅ 카페인 독산점 위생교육 전용 - 연도 + 납기일 */}
                {(detailOption === "doksan_hygiene_edu" || detailOption === "green_hygiene_edu") && (
                  <>
                    <Row label="연도">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={templateState.hygieneYear}
                        onChange={(e) => setTemplateField("hygieneYear", e.target.value.replace(/[^\d]/g, ""))}
                        placeholder="예: 2026"
                        className={templateState.hygieneYear ? styles.loadedData : ""}
                      />
                    </Row>
                    <Row label="납기일">
                      <input
                        type="date"
                        value={templateState.hygieneDueRaw}
                        onChange={(e) => setTemplateField("hygieneDueRaw", e.target.value)}
                        className={templateState.hygieneDueRaw ? styles.loadedData : ""}
                      />
                    </Row>
                  </>
                )}

                {/* ✅ 카페인 독산점 공과금 3종(수도/가스/전기) 공용 - 사용기간(시작/종료) + 납기일
                    (귀속연월은 GASAN_MONTH_TEMPLATES 공용 Row로 이미 처리됨) */}
                {["doksan_water", "doksan_gas", "doksan_electric", "geumnarae_water", "geumnarae_gas", "geumnarae_electric", "green_water", "green_gas", "green_electric"].includes(detailOption) && (
                  <>
                    <Row label="사용기간(시작)">
                      <input
                        type="date"
                        value={templateState.utilityUsageStartRaw}
                        onChange={(e) => setTemplateField("utilityUsageStartRaw", e.target.value)}
                        className={templateState.utilityUsageStartRaw ? styles.loadedData : ""}
                      />
                    </Row>
                    <Row label="사용기간(종료)">
                      <input
                        type="date"
                        value={templateState.utilityUsageEndRaw}
                        onChange={(e) => setTemplateField("utilityUsageEndRaw", e.target.value)}
                        className={templateState.utilityUsageEndRaw ? styles.loadedData : ""}
                      />
                    </Row>
                    <Row label="납기일">
                      <input
                        type="date"
                        value={templateState.utilityDueRaw}
                        onChange={(e) => setTemplateField("utilityDueRaw", e.target.value)}
                        className={templateState.utilityDueRaw ? styles.loadedData : ""}
                      />
                    </Row>
                  </>
                )}

                {/* ✅ 정수기렌탈료 전용 - 월(연도 없는 "M월" 원본 표기라 월 칸만) + 공급가액 + 비고 */}
                {detailOption === "water_rental" && (
                  <>
                    <YearMonthFields
                      label="월"
                      value={templateState.waterMonth}
                      onChange={(v) => setTemplateField("waterMonth", v)}
                      showYear={false}
                    />
                    <Row label="공급가액">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={templateState.waterSupply ? Number(templateState.waterSupply).toLocaleString() : ""}
                        onChange={(e) => setTemplateField("waterSupply", e.target.value.replace(/[^\d]/g, ""))}
                        className={templateState.waterSupply ? styles.loadedData : ""}
                      />
                    </Row>
                    <Row label="비고">
                      <input
                        type="text"
                        value={templateState.waterNote}
                        onChange={(e) => setTemplateField("waterNote", e.target.value)}
                        className={templateState.waterNote ? styles.loadedData : ""}
                      />
                    </Row>
                  </>
                )}

                {/* ✅ 카페인 가산점/독산점/금나래점 포스임대료 전용 - 공급가액 + 비고 (부가세·총계는 자동계산,
                    값 자체가 동일해 state 공유. 금나래점은 기본값 useEffect가 있어 touchedFields 보호 필요) */}
                {(detailOption === "gasan_pos_rental" || detailOption === "doksan_pos_rental" || detailOption === "geumnarae_pos_rental" || detailOption === "green_pos_rental") && (
                  <>
                    <Row label="공급가액">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={templateState.posSupply ? Number(templateState.posSupply).toLocaleString() : ""}
                        onChange={(e) => {
                          setTemplateField("posSupply", e.target.value.replace(/[^\d]/g, ""));
                          setTouchedFields((prev) => ({ ...prev, posSupply: true }));
                        }}
                        className={templateState.posSupply ? styles.loadedData : ""}
                      />
                    </Row>
                    <Row label="비고">
                      <input
                        type="text"
                        value={templateState.posNote}
                        onChange={(e) => setTemplateField("posNote", e.target.value)}
                        className={templateState.posNote ? styles.loadedData : ""}
                      />
                    </Row>
                  </>
                )}

                {/* ✅ 서버이용료 전용 - 금액(공급가) + 비고 */}
                {detailOption === "server_fee" && (
                  <>
                    <Row label="금액">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={templateState.serverSupply ? Number(templateState.serverSupply).toLocaleString() : ""}
                        onChange={(e) => setTemplateField("serverSupply", e.target.value.replace(/[^\d]/g, ""))}
                        className={templateState.serverSupply ? styles.loadedData : ""}
                      />
                    </Row>
                    <Row label="비고">
                      <input
                        type="text"
                        value={templateState.serverNote}
                        onChange={(e) => setTemplateField("serverNote", e.target.value)}
                        className={templateState.serverNote ? styles.loadedData : ""}
                      />
                    </Row>
                  </>
                )}

                {/* ✅ 용역 물품구매 전용 - 대상 + 세부내역 가변 행(상품명/수량/단가/할인/배송비/비고) */}
                {detailOption === "service_purchase" && (
                  <>
                    <Row label="대상">
                      <input
                        type="text"
                        value={templateState.purchaseTarget}
                        onChange={(e) => {
                          setTemplateField("purchaseTarget", e.target.value);
                          setTouchedFields((prev) => ({ ...prev, purchaseTarget: true }));
                        }}
                        placeholder="예: 그린푸줏간 현장 근로자"
                        className={templateState.purchaseTarget ? styles.loadedData : ""}
                      />
                    </Row>
                    {templateState.purchaseRows.map((row, i) => (
                      <Row label={`상품 ${i + 1}`} key={i}>
                        <div className={styles.refundRowGroup}>
                          <input
                            type="text"
                            value={row.name}
                            onChange={(e) => {
                              const next = [...templateState.purchaseRows];
                              next[i] = { ...next[i], name: e.target.value };
                              setTemplateField("purchaseRows", next);
                              setTouchedFields((prev) => ({ ...prev, purchaseRows: true }));
                            }}
                            placeholder="상품명"
                          />
                          <input
                            type="text"
                            inputMode="numeric"
                            value={row.qty}
                            onChange={(e) => {
                              const next = [...templateState.purchaseRows];
                              next[i] = { ...next[i], qty: e.target.value.replace(/[^\d]/g, "") };
                              setTemplateField("purchaseRows", next);
                              setTouchedFields((prev) => ({ ...prev, purchaseRows: true }));
                            }}
                            placeholder="수량"
                          />
                          <input
                            type="text"
                            inputMode="numeric"
                            value={row.price ? Number(row.price).toLocaleString() : ""}
                            onChange={(e) => {
                              const next = [...templateState.purchaseRows];
                              next[i] = { ...next[i], price: e.target.value.replace(/[^\d]/g, "") };
                              setTemplateField("purchaseRows", next);
                              setTouchedFields((prev) => ({ ...prev, purchaseRows: true }));
                            }}
                            placeholder="단가"
                          />
                          <input
                            type="text"
                            inputMode="numeric"
                            value={row.discount ? Number(row.discount).toLocaleString() : ""}
                            onChange={(e) => {
                              const next = [...templateState.purchaseRows];
                              next[i] = { ...next[i], discount: e.target.value.replace(/[^\d]/g, "") };
                              setTemplateField("purchaseRows", next);
                              setTouchedFields((prev) => ({ ...prev, purchaseRows: true }));
                            }}
                            placeholder="할인"
                          />
                          <input
                            type="text"
                            inputMode="numeric"
                            value={row.shipping ? Number(row.shipping).toLocaleString() : ""}
                            onChange={(e) => {
                              const next = [...templateState.purchaseRows];
                              next[i] = { ...next[i], shipping: e.target.value.replace(/[^\d]/g, "") };
                              setTemplateField("purchaseRows", next);
                              setTouchedFields((prev) => ({ ...prev, purchaseRows: true }));
                            }}
                            placeholder="배송비"
                          />
                          <input
                            type="text"
                            value={row.note}
                            onChange={(e) => {
                              const next = [...templateState.purchaseRows];
                              next[i] = { ...next[i], note: e.target.value };
                              setTemplateField("purchaseRows", next);
                              setTouchedFields((prev) => ({ ...prev, purchaseRows: true }));
                            }}
                            placeholder="비고"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setTemplateField("purchaseRows", templateState.purchaseRows.filter((_, ri) => ri !== i));
                              setTouchedFields((prev) => ({ ...prev, purchaseRows: true }));
                            }}
                            disabled={templateState.purchaseRows.length <= 1}
                          >
                            ✕
                          </button>
                        </div>
                      </Row>
                    ))}
                    <Row label="">
                      <button
                        type="button"
                        className={styles.refundAddBtn}
                        onClick={() => {
                          setTemplateField("purchaseRows", [
                            ...templateState.purchaseRows,
                            { name: "", qty: "", price: "", discount: "", shipping: "", note: "" },
                          ]);
                          setTouchedFields((prev) => ({ ...prev, purchaseRows: true }));
                        }}
                      >
                        + 상품 추가
                      </button>
                    </Row>
                  </>
                )}

                {/* ✅ 위탁수수료(보건/안전) 전용 - 귀속연월 + 위탁업체 (문구는 템플릿에 고정, 귀속연월만 입력하면 됨) */}
                {(detailOption === "service_commission_health" || detailOption === "service_commission_safety") && (
                  <>
                    <Row label="귀속연월">
                      <input
                        type="text"
                        value={templateState.commissionMonth}
                        onChange={(e) => {
                          setTemplateField("commissionMonth", e.target.value);
                          setTouchedFields((prev) => ({ ...prev, commissionMonth: true }));
                        }}
                        placeholder="예: 2026년 6월"
                        className={templateState.commissionMonth ? styles.loadedData : ""}
                      />
                    </Row>
                    <Row label="위탁업체">
                      <input
                        type="text"
                        value={templateState.commissionVendor}
                        onChange={(e) => {
                          setTemplateField("commissionVendor", e.target.value);
                          setTouchedFields((prev) => ({ ...prev, commissionVendor: true }));
                        }}
                        className={templateState.commissionVendor ? styles.loadedData : ""}
                      />
                    </Row>
                  </>
                )}

                {/* ✅ 일용직급여 전용 - 귀속연월 + 세부내역 가변 행(근무자/근무지/근무일자/지급/공제, 실지급은 자동계산) */}
                {detailOption === "service_daily_wage" && (
                  <>
                    <Row label="귀속연월">
                      <input
                        type="text"
                        value={templateState.dailyWageMonth}
                        onChange={(e) => {
                          setTemplateField("dailyWageMonth", e.target.value);
                          setTouchedFields((prev) => ({ ...prev, dailyWageMonth: true }));
                        }}
                        placeholder="예: 2026년 5월"
                        className={templateState.dailyWageMonth ? styles.loadedData : ""}
                      />
                    </Row>
                    {templateState.dailyWageRows.map((row, i) => (
                      <Row label={`근무자 ${i + 1}`} key={i}>
                        <div className={styles.refundRowGroup}>
                          <input
                            type="text"
                            value={row.name}
                            onChange={(e) => {
                              const next = [...templateState.dailyWageRows];
                              next[i] = { ...next[i], name: e.target.value };
                              setTemplateField("dailyWageRows", next);
                              setTouchedFields((prev) => ({ ...prev, dailyWageRows: true }));
                            }}
                            placeholder="근무자"
                          />
                          <input
                            type="text"
                            value={row.site}
                            onChange={(e) => {
                              const next = [...templateState.dailyWageRows];
                              next[i] = { ...next[i], site: e.target.value };
                              setTemplateField("dailyWageRows", next);
                              setTouchedFields((prev) => ({ ...prev, dailyWageRows: true }));
                            }}
                            placeholder="근무지"
                          />
                          <input
                            type="text"
                            value={row.workDate}
                            onChange={(e) => {
                              const next = [...templateState.dailyWageRows];
                              next[i] = { ...next[i], workDate: e.target.value };
                              setTemplateField("dailyWageRows", next);
                              setTouchedFields((prev) => ({ ...prev, dailyWageRows: true }));
                            }}
                            placeholder="근무일자"
                          />
                          <input
                            type="text"
                            inputMode="numeric"
                            value={row.pay ? Number(row.pay).toLocaleString() : ""}
                            onChange={(e) => {
                              const next = [...templateState.dailyWageRows];
                              next[i] = { ...next[i], pay: e.target.value.replace(/[^\d]/g, "") };
                              setTemplateField("dailyWageRows", next);
                              setTouchedFields((prev) => ({ ...prev, dailyWageRows: true }));
                            }}
                            placeholder="지급"
                          />
                          <input
                            type="text"
                            inputMode="numeric"
                            value={row.deduction ? Number(row.deduction).toLocaleString() : ""}
                            onChange={(e) => {
                              const next = [...templateState.dailyWageRows];
                              next[i] = { ...next[i], deduction: e.target.value.replace(/[^\d]/g, "") };
                              setTemplateField("dailyWageRows", next);
                              setTouchedFields((prev) => ({ ...prev, dailyWageRows: true }));
                            }}
                            placeholder="공제"
                          />
                          <input
                            type="text"
                            readOnly
                            value={calcDailyWageRow(row).net.toLocaleString()}
                            placeholder="실지급(자동)"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setTemplateField("dailyWageRows", templateState.dailyWageRows.filter((_, ri) => ri !== i));
                              setTouchedFields((prev) => ({ ...prev, dailyWageRows: true }));
                            }}
                            disabled={templateState.dailyWageRows.length <= 1}
                          >
                            ✕
                          </button>
                        </div>
                      </Row>
                    ))}
                    <Row label="">
                      <button
                        type="button"
                        className={styles.refundAddBtn}
                        onClick={() => {
                          setTemplateField("dailyWageRows", [
                            ...templateState.dailyWageRows,
                            { name: "", site: "", workDate: "", pay: "", deduction: "" },
                          ]);
                          setTouchedFields((prev) => ({ ...prev, dailyWageRows: true }));
                        }}
                      >
                        + 근무자 추가
                      </button>
                    </Row>
                  </>
                )}

                {/* ✅ 템플릿별 입력 필드 자동 생성 (fields 중 input:true인 것들 — 예산과목, 일시, 장소, 집행대상 ...)
                    f.default에 "비어있지 않은" 고정 문구가 있는 필드(예산과목 등 상용구)만 잠근다.
                    일시/장소/집행대상처럼 default가 없어 매번 사용자가 직접 입력해야 하는 필드까지
                    잠기면 안 되므로, 단순히 default 속성 존재 여부(!== undefined)가 아니라
                    "값이 실제로 있는지"(!!f.default)로 판단한다.
                    단, 소모품구매(supplies)의 지출방법(method)은 결제방식 변경 시 자동 갱신되며
                    touchedFields로 사용자 수정을 보호하는 별도 로직이 있어 예외로 둔다. */}
                {(EXPENSE_TEMPLATES[detailOption]?.fields || [])
                  .filter((f) => f.input && !(detailOption === "utility_fee" && f.key === "dueDate"))
                  .map((f) => {
                    const isFixedDefault =
                      !!f.default && !(detailOption === "supplies" && f.key === "method");
                    return (
                      <Row label={f.label.replace(/\s+/g, "")} key={f.key}>
                        <input
                          type="text"
                          value={templateInputs[f.key] || ""}
                          onChange={(e) => {
                            if (isFixedDefault) return;
                            setTemplateInput(f.key, e.target.value);
                          }}
                          readOnly={isFixedDefault}
                          placeholder={f.placeholder || ""}
                          className={templateInputs[f.key] ? styles.loadedData : ""}
                        />
                      </Row>
                    );
                  })}


                {/* ✅ 붙임 컴포넌트 (기존 인라인 코드 → 컴포넌트로 교체) */}
                <AttachmentSection
                  attachments={attachments}
                  setAttachments={setAttachments}
                  documentType={documentType}
                  category={category}
                  detailOption={detailOption}
                  vendor={vendorInput}          // ✅ 거래처명 전달

                  autoFill={isDataLoaded}
                />

                {/* 본문/비고 - 세로 유지 */}
                <Row label="본문/비고">
                  <textarea 
                    rows={3} 
                    value={desc} 
                    onChange={(e) => setDesc(e.target.value)} 
                    placeholder="관련 메모를 입력하세요."
                    className={desc ? styles.loadedData : ""}
                  />
                </Row>
              </>
            )}
          </form>
        </div>
  );
}
